import React, { useEffect, useState } from 'react';
import { XIcon } from 'lucide-react';
interface Vehicle {
  id: number;
  name: string;
  model: string;
  price: string;
  image: string;
  year: string;
  mileage: string;
  condition: 'New' | 'Used';
  transmission: string;
  fuelType: string;
}
interface VehicleFormProps {
  vehicle?: Vehicle;
  onSave: (vehicle: Omit<Vehicle, 'id'>) => void;
  onCancel: () => void;
}
export function VehicleForm({
  vehicle,
  onSave,
  onCancel
}: VehicleFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    model: '',
    price: '',
    year: '',
    mileage: '',
    condition: 'New' as 'New' | 'Used',
    transmission: '',
    fuelType: '',
    image: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    if (vehicle) {
      setFormData({
        name: vehicle.name,
        model: vehicle.model,
        price: vehicle.price,
        year: vehicle.year,
        mileage: vehicle.mileage,
        condition: vehicle.condition,
        transmission: vehicle.transmission,
        fuelType: vehicle.fuelType,
        image: vehicle.image
      });
    }
  }, [vehicle]);
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.model.trim()) newErrors.model = 'Model is required';
    if (!formData.price.trim()) newErrors.price = 'Price is required';
    if (!formData.year.trim()) newErrors.year = 'Year is required';
    if (!formData.mileage.trim()) newErrors.mileage = 'Mileage is required';
    if (!formData.transmission.trim()) newErrors.transmission = 'Transmission is required';
    if (!formData.fuelType.trim()) newErrors.fuelType = 'Fuel type is required';
    if (!formData.image.trim()) newErrors.image = 'Image URL is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };
  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };
  return <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" data-id="element-28">
      <div className="bg-[var(--color-black-rich)] rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-29">
        <div className="sticky top-0 bg-[var(--color-black-rich)] border-b border-gray-800 p-6 flex items-center justify-between" data-id="element-30">
          <h2 className="text-2xl font-bold text-white" data-id="element-31">
            {vehicle ? 'Edit Vehicle' : 'Add New Vehicle'}
          </h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-white transition-colors" aria-label="Close form" data-id="element-32">
            <XIcon className="w-6 h-6" aria-hidden="true" data-id="element-33" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6" data-id="element-34">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-35">
            {/* Name */}
            <div data-id="element-36">
              <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-37">
                Brand Name *
              </label>
              <input id="name" type="text" value={formData.name} onChange={e => handleChange('name', e.target.value)} className="w-full px-4 py-2 bg-[var(--color-black-soft)] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[var(--color-gold)] transition-colors" placeholder="e.g., Mercedes-Benz" data-id="element-38" />
              {errors.name && <p className="text-red-400 text-sm mt-1" data-id="element-39">{errors.name}</p>}
            </div>

            {/* Model */}
            <div data-id="element-40">
              <label htmlFor="model" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-41">
                Model *
              </label>
              <input id="model" type="text" value={formData.model} onChange={e => handleChange('model', e.target.value)} className="w-full px-4 py-2 bg-[var(--color-black-soft)] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[var(--color-gold)] transition-colors" placeholder="e.g., S-Class S 500" data-id="element-42" />
              {errors.model && <p className="text-red-400 text-sm mt-1" data-id="element-43">{errors.model}</p>}
            </div>

            {/* Price */}
            <div data-id="element-44">
              <label htmlFor="price" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-45">
                Price *
              </label>
              <input id="price" type="text" value={formData.price} onChange={e => handleChange('price', e.target.value)} className="w-full px-4 py-2 bg-[var(--color-black-soft)] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[var(--color-gold)] transition-colors" placeholder="e.g., $125,000" data-id="element-46" />
              {errors.price && <p className="text-red-400 text-sm mt-1" data-id="element-47">{errors.price}</p>}
            </div>

            {/* Year */}
            <div data-id="element-48">
              <label htmlFor="year" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-49">
                Year *
              </label>
              <input id="year" type="text" value={formData.year} onChange={e => handleChange('year', e.target.value)} className="w-full px-4 py-2 bg-[var(--color-black-soft)] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[var(--color-gold)] transition-colors" placeholder="e.g., 2024" data-id="element-50" />
              {errors.year && <p className="text-red-400 text-sm mt-1" data-id="element-51">{errors.year}</p>}
            </div>

            {/* Mileage */}
            <div data-id="element-52">
              <label htmlFor="mileage" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-53">
                Mileage *
              </label>
              <input id="mileage" type="text" value={formData.mileage} onChange={e => handleChange('mileage', e.target.value)} className="w-full px-4 py-2 bg-[var(--color-black-soft)] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[var(--color-gold)] transition-colors" placeholder="e.g., 3,200 km" data-id="element-54" />
              {errors.mileage && <p className="text-red-400 text-sm mt-1" data-id="element-55">{errors.mileage}</p>}
            </div>

            {/* Condition */}
            <div data-id="element-56">
              <label htmlFor="condition" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-57">
                Condition *
              </label>
              <select id="condition" value={formData.condition} onChange={e => handleChange('condition', e.target.value)} className="w-full px-4 py-2 bg-[var(--color-black-soft)] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[var(--color-gold)] transition-colors" data-id="element-58">
                <option value="New" data-id="element-59">New</option>
                <option value="Used" data-id="element-60">Used</option>
              </select>
            </div>

            {/* Transmission */}
            <div data-id="element-61">
              <label htmlFor="transmission" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-62">
                Transmission *
              </label>
              <input id="transmission" type="text" value={formData.transmission} onChange={e => handleChange('transmission', e.target.value)} className="w-full px-4 py-2 bg-[var(--color-black-soft)] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[var(--color-gold)] transition-colors" placeholder="e.g., Automatic" data-id="element-63" />
              {errors.transmission && <p className="text-red-400 text-sm mt-1" data-id="element-64">
                  {errors.transmission}
                </p>}
            </div>

            {/* Fuel Type */}
            <div data-id="element-65">
              <label htmlFor="fuelType" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-66">
                Fuel Type *
              </label>
              <input id="fuelType" type="text" value={formData.fuelType} onChange={e => handleChange('fuelType', e.target.value)} className="w-full px-4 py-2 bg-[var(--color-black-soft)] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[var(--color-gold)] transition-colors" placeholder="e.g., Petrol" data-id="element-67" />
              {errors.fuelType && <p className="text-red-400 text-sm mt-1" data-id="element-68">{errors.fuelType}</p>}
            </div>
          </div>

          {/* Image URL */}
          <div data-id="element-69">
            <label htmlFor="image" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-70">
              Image URL *
            </label>
            <input id="image" type="text" value={formData.image} onChange={e => handleChange('image', e.target.value)} className="w-full px-4 py-2 bg-[var(--color-black-soft)] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[var(--color-gold)] transition-colors" placeholder="https://images.unsplash.com/..." data-id="element-71" />
            {errors.image && <p className="text-red-400 text-sm mt-1" data-id="element-72">{errors.image}</p>}
          </div>

          {/* Actions */}
          <div className="flex gap-4 pt-4" data-id="element-73">
            <button type="submit" className="flex-1 px-6 py-3 bg-[var(--color-gold)] text-black font-semibold rounded-lg hover:bg-[var(--color-gold)]/90 transition-colors" data-id="element-74">
              {vehicle ? 'Update Vehicle' : 'Add Vehicle'}
            </button>
            <button type="button" onClick={onCancel} className="px-6 py-3 bg-[var(--color-black-soft)] text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors" data-id="element-75">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>;
}